import { useState } from "react";
import { i18n } from "../../translate/i18n";
import {
  getPromptMedia,
  uploadPromptMedia,
  deletePromptMedia,
  addPromptMediaUrl,
} from "../../services/prompt.api";

// Infiere el mediaType (image/video/audio/document) desde la extensión de la URL.
// El backend (PromptMediaController) lo exige para registrar media por URL.
const inferMediaTypeFromUrl = (u) => {
  const ext = String(u || "").split(/[?#]/)[0].split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif"].includes(ext)) return "image";
  if (["mp4", "mov", "avi", "webm", "mkv", "m4v"].includes(ext)) return "video";
  if (["mp3", "wav", "ogg", "m4a", "aac", "flac"].includes(ext)) return "audio";
  return "document";
};

// D8.3.4a — Gestión de archivos multimedia del prompt (subida/URL/eliminar/reordenar).
export default function usePromptMedia(promptId) {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [urlModalOpen, setUrlModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(null);
  const [mediaMessage, setMediaMessage] = useState({ type: null, text: null });

    const fetchMediaFiles = async (id) => {
        try {
            console.log("🔵 [fetchMediaFiles] Cargando archivos para promptId:", id);
            // NO cambiar loadingMedia aquí para evitar conflictos con otras operaciones
            const { data } = await getPromptMedia(id);
            const files = data || [];
            
            // ✅ Limpiar y normalizar datos recibidos del backend
            const cleanedFiles = files.map(file => {
                // ✅ Limpiar mediaName: eliminar cualquier carácter no imprimible o espacios extra
                let cleanName = file.mediaName;
                if (cleanName) {
                    cleanName = String(cleanName)
                        .replace(/[\x00-\x1F\x7F-\x9F]/g, '') // Eliminar caracteres de control
                        .replace(/\0/g, '') // Eliminar null bytes
                        .trim();
                }
                
                // ✅ Limpiar mediaSize: asegurar que sea número
                let cleanSize = file.mediaSize;
                if (cleanSize) {
                    if (typeof cleanSize === 'string') {
                        cleanSize = parseInt(cleanSize.replace(/[^0-9]/g, ''), 10); // Solo números
                    }
                    cleanSize = Number(cleanSize);
                    if (isNaN(cleanSize)) cleanSize = null;
                }
                
                return {
                    ...file,
                    mediaName: cleanName || null,
                    mediaSize: cleanSize
                };
            });
            
            setMediaFiles(cleanedFiles);
            return cleanedFiles;
        } catch (err) {
            console.error("❌ [fetchMediaFiles] Error cargando archivos multimedia:", err);
            setMediaFiles([]);
            return []; // ✅ Retornar array vacío en caso de error
        }
    };

    // ✅ Función para subir archivo local (ahora soporta múltiples archivos)
    const handleFileUpload = async (files) => {
        console.log("🔵 [handleFileUpload] Iniciando upload, promptId:", promptId, "files:", files);
        console.log("🔵 [handleFileUpload] files.length:", files?.length, "files type:", typeof files);
        
        if (!promptId) {
            console.error("❌ [handleFileUpload] No hay promptId");
            setMediaMessage({ type: 'error', text: i18n.t("promptModal.media.savePromptFirst") });
            return;
        }

        // ✅ CRÍTICO: Convertir FileList a Array INMEDIATAMENTE (antes de cualquier async)
        let filesArray = [];
        if (files && files.length > 0) {
            // Si es FileList, convertirlo a Array
            if (files instanceof FileList) {
                filesArray = Array.from(files);
            } else if (Array.isArray(files)) {
                filesArray = files;
            } else {
                console.error("❌ [handleFileUpload] Tipo de archivo no reconocido:", typeof files);
                setMediaMessage({ type: 'error', text: i18n.t("promptModal.media.unknownFileType") });
                return;
            }
        } else {
            console.error("❌ [handleFileUpload] No hay archivos para subir");
            setMediaMessage({ type: 'error', text: i18n.t("promptModal.media.noFilesSelected") });
            return;
        }
        
        console.log("✅ [handleFileUpload] Archivos convertidos a Array:", filesArray.length);
        console.log("✅ [handleFileUpload] Nombres de archivos:", filesArray.map(f => f.name));

        try {
            // ✅ CRÍTICO: Recargar archivos desde el backend ANTES de validar (estado real)
            console.log("🔵 [handleFileUpload] Recargando archivos desde BD...");
            const currentFiles = await fetchMediaFiles(promptId);
            console.log("✅ [handleFileUpload] Archivos actuales en BD:", currentFiles.length);

        // ✅ Validar que no se excedan 6 archivos en total (usando valor retornado directamente)
        const totalAfterUpload = currentFiles.length + filesArray.length;
        if (totalAfterUpload > 6) {
            setMediaMessage({ 
                type: 'error', 
                text: i18n.t("promptModal.media.maxSixWithCounts", {
                    current: currentFiles.length,
                    adding: filesArray.length,
                    remaining: Math.max(0, 6 - currentFiles.length),
                }),
            });
            return;
        }

        // Validar cada archivo
        const validFiles = [];
        const errors = [];

        for (const file of filesArray) {
            // Validar tamaño según tipo
            const fileType = file.type.split('/')[0];
            const maxSizes = {
                image: 10 * 1024 * 1024, // 10 MB
                video: 15 * 1024 * 1024, // 15 MB
                audio: 10 * 1024 * 1024, // 10 MB
                application: 15 * 1024 * 1024, // 15 MB (documentos)
            };

            const maxSize = maxSizes[fileType] || 15 * 1024 * 1024;
            if (file.size > maxSize) {
                const maxMB = maxSize / (1024 * 1024);
                errors.push(i18n.t("promptModal.media.fileTooBig", { name: file.name, maxMb: maxMB }));
                continue;
            }

            validFiles.push(file);
        }

        // Mostrar errores si hay
        if (errors.length > 0) {
            setMediaMessage({ 
                type: 'error', 
                text: i18n.t("promptModal.media.someRejected", { list: errors.join(", ") }),
            });
        }

        if (validFiles.length === 0) {
            return;
        }

            // Subir archivos uno por uno (recargando estado después de cada uno)
            setLoadingMedia(true);
            setMediaMessage({ type: null, text: null }); // Limpiar mensaje anterior
            const uploadedFiles = [];
            const failedFiles = [];

            console.log("🔵 [handleFileUpload] Iniciando subida de", validFiles.length, "archivos válidos");

            for (let i = 0; i < validFiles.length; i++) {
                const file = validFiles[i];
                console.log(`🔵 [handleFileUpload] Procesando archivo ${i + 1}/${validFiles.length}:`, file.name);
                
                // ✅ Recargar estado actual desde BD antes de cada validación
                const currentFiles = await fetchMediaFiles(promptId);
                
                // ✅ Validar usando el valor retornado directamente (no el estado)
                if (currentFiles.length >= 6) {
                    console.warn("⚠️ [handleFileUpload] Límite alcanzado:", currentFiles.length);
                    setMediaMessage({ 
                        type: 'error', 
                        text: i18n.t("promptModal.media.maxSixNoMore", { count: currentFiles.length }),
                    });
                    break;
                }

                const formData = new FormData();
                formData.append("file", file);
                
                try {
                    console.log(`🔵 [handleFileUpload] Enviando archivo ${file.name} al servidor...`);
                    const { data } = await uploadPromptMedia(promptId, formData, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    });
                    
                    console.log(`✅ [handleFileUpload] Archivo ${file.name} subido exitosamente:`, data);
                    uploadedFiles.push(data);
                    
                    // ✅ Recargar archivos desde BD después de cada éxito
                    await fetchMediaFiles(promptId);
                } catch (fileError) {
                    // Si falla un archivo, continuar con los demás
                    const errorMsg = fileError.response?.data?.message || fileError.message || i18n.t("promptModal.apiTest.unknownError");
                    console.error(`❌ [handleFileUpload] Error subiendo ${file.name}:`, fileError);
                    failedFiles.push({ name: file.name, error: errorMsg });
                }
            }
            
            console.log("🔵 [handleFileUpload] Proceso completado. Exitosos:", uploadedFiles.length, "Fallidos:", failedFiles.length);
            
            // ✅ Mostrar mensajes en el modal
            if (uploadedFiles.length > 0 && failedFiles.length === 0) {
                setMediaMessage({ 
                    type: 'success', 
                    text: i18n.t("promptModal.media.uploadSuccessCount", { count: uploadedFiles.length }),
                });
            } else if (uploadedFiles.length > 0 && failedFiles.length > 0) {
                setMediaMessage({ 
                    type: 'error', 
                    text: i18n.t("promptModal.media.uploadPartial", {
                        uploaded: uploadedFiles.length,
                        failed: failedFiles.length,
                        names: failedFiles.map(f => f.name).join(", "),
                    }),
                });
            } else if (failedFiles.length > 0) {
                setMediaMessage({ 
                    type: 'error', 
                    text: i18n.t("promptModal.media.uploadFailedDetail", {
                        details: failedFiles.map(f => `${f.name} (${f.error})`).join(", "),
                    }),
                });
            } else if (uploadedFiles.length === 0 && failedFiles.length === 0) {
                setMediaMessage({ 
                    type: 'error', 
                    text: i18n.t("promptModal.media.uploadNone"),
                });
            }
        } catch (err) {
            console.error("❌ [handleFileUpload] Error inesperado:", err);
            setMediaMessage({ 
                type: 'error', 
                text: i18n.t("promptModal.media.unexpectedError", {
                    message: err.message || i18n.t("promptModal.apiTest.unknownError"),
                }),
            });
        } finally {
            setLoadingMedia(false);
            console.log("🔵 [handleFileUpload] Finalizado");
        }
    };

    // ✅ Función para eliminar archivo
    const handleDeleteMedia = async (mediaId) => {
        if (!promptId) return;
        
        try {
            setLoadingMedia(true);
            setMediaMessage({ type: null, text: null }); // Limpiar mensaje
            await deletePromptMedia(promptId, mediaId);
            await fetchMediaFiles(promptId); // ✅ Recargar desde BD
            setMediaMessage({ type: 'success', text: i18n.t("promptModal.media.fileDeleted") });
        } catch (err) {
            setMediaMessage({ 
                type: 'error', 
                text: i18n.t("promptModal.media.deleteFileError", {
                    message: err.response?.data?.message || err.message || i18n.t("promptModal.apiTest.unknownError"),
                }),
            });
        } finally {
            setLoadingMedia(false);
        }
    };

    // ✅ Función para agregar URL externa
    const handleAddUrl = async (url, caption) => {
        if (!promptId) {
            setMediaMessage({ type: 'error', text: i18n.t("promptModal.media.savePromptFirst") });
            return;
        }

        // ✅ Recargar estado actual desde BD
        const currentFiles = await fetchMediaFiles(promptId);

        if (currentFiles.length >= 6) {
            setMediaMessage({ type: 'error', text: i18n.t("promptModal.media.maxSixFiles") });
            return;
        }

        try {
            setLoadingMedia(true);
            setMediaMessage({ type: null, text: null }); // Limpiar mensaje
            const { data } = await addPromptMediaUrl(promptId, {
                mediaUrl: url,
                mediaType: inferMediaTypeFromUrl(url),
                caption: caption || null,
            });
            await fetchMediaFiles(promptId); // ✅ Recargar desde BD
            setMediaMessage({ type: 'success', text: i18n.t("promptModal.media.urlAdded") });
            setUrlModalOpen(false);
        } catch (err) {
            setMediaMessage({ 
                type: 'error', 
                text: i18n.t("promptModal.media.urlAddError", {
                    message: err.response?.data?.message || err.message || i18n.t("promptModal.apiTest.unknownError"),
                }),
            });
        } finally {
            setLoadingMedia(false);
        }
    };

    // ✅ Función para reordenar (drag & drop básico)
    const handleReorder = (index, action) => {
        if (action === "start") {
            setIsDragging(index);
        } else if (action === "end") {
            setIsDragging(null);
        }
    };

  return {
    mediaFiles, setMediaFiles, loadingMedia, urlModalOpen, setUrlModalOpen,
    isDragging, setIsDragging, mediaMessage, setMediaMessage, fetchMediaFiles,
    handleFileUpload, handleDeleteMedia, handleAddUrl, handleReorder,
  };
}
